import { Injectable } from '@angular/core';
import { Globals } from 'app/_globals';

export type WorkflowCategory = 'contractor-cert' | 'worker-competency' | 'restriction' | 'asset-booking';

@Injectable()
export class WorkflowAccessService {
  // Role IDs used throughout workflow visibility checks:
  // 3 = site-owner
  // 4 = support
  // 5 = admin
  // 7 = site-delegate
  // 9 = power-station-owner
  // 11 = site-delegate (control admin)
  // 12 = site-delegate (regional manager)
  // 13 = authorising officer (worker competency only)
  constructor(private globals: Globals) {}

  public getVisibleWorkflowCategories(): WorkflowCategory[] {
    // A pure authorising officer only sees worker competency approvals.
    if (this.globals.user.role === 13) {
      return ['worker-competency'];
    }

    const categories: WorkflowCategory[] = ['contractor-cert'];

    if (this.canViewRestrictionWorkflow()) {
      categories.push('restriction');
    }

    if (this.canViewWorkerCompetencyWorkflow()) {
      categories.push('worker-competency');
    }

    if (this.canViewAssetSchedulingWorkflow()) {
      categories.push('asset-booking');
    }

    return categories;
  }

  public canViewWorkerCompetencyWorkflow(): boolean {
    // Authorising officers always see worker competency approvals.
    if (this.globals.user.role === 13) {
      return true;
    }

    if (this.globals.user.routeRole !== 'site-delegate') {
      return true;
    }

    return !!this.globals.user.canEditCompetencies;
  }

  public canOpenWorkerCompetencyManagement(): boolean {
    const routeRole = this.globals.user.routeRole;

    if (routeRole === 'authorising-officer') {
      return true;
    }

    if (routeRole === 'admin' || routeRole === 'support' || routeRole === 'site-owner' || routeRole === 'power-station-owner') {
      return true;
    }

    if (routeRole === 'site-delegate') {
      return !!this.globals.user.canEditCompetencies;
    }

    return false;
  }

  public canViewAssetSchedulingWorkflow(): boolean {
    // Power-station users do not participate in asset scheduling approvals.
    if (this.globals.user.role === 4 || this.globals.user.role === 5) {
      return true;
    }

    if (this.globals.user.role === 3 || this.globals.user.role === 7 || this.globals.user.role === 11 || this.globals.user.role === 12) {
      return !!this.globals.siteOwnerToggles.allowAssetScheduling;
    }

    return false;
  }

  public canViewRestrictionWorkflow(): boolean {
    // Power-station users do not participate in restriction approvals.
    if (this.globals.user.role === 4 || this.globals.user.role === 5) {
      return true;
    }

    if (this.globals.user.role === 3 || this.globals.user.role === 7 || this.globals.user.role === 11 || this.globals.user.role === 12) {
      return !!this.globals.siteOwnerToggles.allowRestrictionsWorkflow;
    }

    return false;
  }

  public canShowWorkflowApprovalEntryPoint(): boolean {
    return this.globals.user.routeRole === 'admin'
      || this.globals.user.routeRole === 'support'
      || this.globals.user.routeRole === 'site-owner'
      || this.globals.user.routeRole === 'site-delegate'
      || this.globals.user.routeRole === 'power-station-owner'
      || this.globals.user.routeRole === 'authorising-officer';
  }
}
